import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { cloudinaryUrl } from '../utils/cloudinaryUrl';
import { isShortlisted, toggleShortlist } from '../utils/buyerCollections';
import { trackEvent } from '../utils/analytics';
import './VehicleCard.css';
import { vehiclePath } from '../utils/vehicleSlug';

// `titleAs` lets a caller render the card title as something other than an <h2>.
// Listing pages want h2s; a card embedded mid-article does not, because those
// titles are labels, not headings of the document.
const VehicleCard = ({ v, ribbon, tag, testId, eager, source, titleAs: TitleTag = 'h2', lcp }) => {
  const [saved, setSaved] = useState(() => isShortlisted(v.id));

  useEffect(() => {
    const sync = () => setSaved(isShortlisted(v.id));
    window.addEventListener('bst-collections', sync);
    return () => window.removeEventListener('bst-collections', sync);
  }, [v.id]);

  const onHeart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSaved(toggleShortlist(v));
  };

  const onOpen = () => {
    if (source) {
      trackEvent('vehicle_card_click', { source, vehicle_id: v.id, vehicle_title: v.title });
    }
  };

  // eager = in the first screenful, don't lazy-load. lcp = THE first card, the
  // one image that decides Largest Contentful Paint — only it gets
  // fetchpriority=high. Four high-priority images made the first compete with
  // the other three for bandwidth on a phone that shows one card per row.
  return (
    <Link to={vehiclePath(v)} onClick={onOpen} className={`vehicle-card${tag === 'sponsored' ? ' sponsored-card' : ''}`} data-testid={testId || `card-${v.id}`}>
      <div className="card-img-wrap">
        <img src={cloudinaryUrl(v.images?.[0] || '/vehicle-placeholder.svg', 600)} alt={v.title} loading={eager || lcp ? 'eager' : 'lazy'} fetchpriority={lcp ? 'high' : undefined} />
        {ribbon === 'featured' && <div className="featured-ribbon">Featured</div>}
        {tag === 'sponsored' && <div className="sponsored-tag">Sponsored</div>}
        {v.status === 'sold' && <div className="sold-overlay">SOLD</div>}
        <button
          className={`card-heart ${saved ? 'saved' : ''}`}
          onClick={onHeart}
          aria-label={saved ? 'Remove from shortlist' : 'Add to shortlist'}
          aria-pressed={saved}
          data-testid={`heart-${v.id}`}
        >
          <Heart size={18} />
        </button>
      </div>
      <div className="card-body">
        <TitleTag className="card-title">{v.title}</TitleTag>
        <div className="card-details">
          <span>{v.year}</span><span>{v.km_run?.toLocaleString()} km</span><span>{v.city}</span>
        </div>
        <div className="card-bottom">
          <span className="card-price">₹{Number(v.price_final).toLocaleString('en-IN')}</span>
          <span className={`badge-pill badge-${v.badge_type}`}>
            {v.badge_type === 'bst_listing' ? 'BST' : 'Direct Seller'}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default VehicleCard;
